import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import api from '@/services/api';
import type { EcomOrder } from '@/api/ecommerce/orders';
import type { PackingLabelMap, ShipmentRequestState } from '@tcr-erp/shared';
import type { BatchPackingCompletionResponse, EcomScanLogEntry } from '@/api/ecommerce/fulfillment';
import type { BatchActionResult, WarehouseWorkspaceFilter, WarehouseWorkspaceKey } from '@/api/ecommerce/warehouse-fulfillment';
import { FulfillmentProgress, ShippingPartnerName } from '@tcr-erp/shared';

export { FulfillmentProgress, ShippingPartnerName };

export interface OffPlatformPackingScanPayload {
  barcode: string;
  scan_session_id: string;
  quantity?: number;
  camera_video_ref?: string;
}

export interface OffPlatformPackingScanResult {
  success: true;
  matched: boolean;
  status: 'matched' | 'not_found' | 'invalid_state';
  message: string;
  matched_sku?: string;
  required_quantity?: number;
  scanned_quantity?: number;
  remaining_quantity?: number;
  all_scanned?: boolean;
  all_packed?: boolean;
}

type OffPlatformPackingScanLog = Omit<EcomScanLogEntry, 'scan_stage'>;

export interface OffPlatformFulfillmentOrder {
  _id: string;
  order_number: string;
  state: string;
  store_id?: string | { _id: string };
  shipping_state?: string | null;
  fulfillment_progress?: FulfillmentProgress | null;
  shipment_request?: ShipmentRequestState;
  shipment_request_timed_out?: boolean;
  display_status?: string;
  display_status_key?: string;
  display_substatus?: string;
  display_substatus_type?: 'secondary' | 'warning' | 'danger';
  is_printed?: boolean;
  items?: EcomOrder['items'];
  fulfillment?: {
    status?: string;
    label_printed?: boolean;
    label_printed_at?: string;
    packed_at?: string;
    packing_scan_logs?: OffPlatformPackingScanLog[];
  };
  created_at?: string;
  receiver_name?: string;
  /** Ghi chú vận chuyển POS, lưu ở top-level order.shipping_note. */
  shipping_note?: string;
  /**
   * Ghi chú ĐƠN HÀNG POS (`notes.user_note`) — ô "Thêm ghi chú đơn hàng..."
   * ở cột phải màn POS. Khác hẳn `shipping_note` phía trên; `internal_note`
   * trong cùng cụm này là chuỗi hệ thống tự sinh, không phải người dùng gõ.
   */
  notes?: EcomOrder['notes'];
  /** Id thẻ gán ở màn Đơn hàng — danh mục `order_tags`, KHÔNG phải thẻ ECom. */
  tags?: string[];
  /** Đơn bị "Bỏ qua" ở màn Đơn hàng — chỉ ẩn khỏi tab cần xử lý, không đụng tồn (TCR-2445). */
  skipped?: boolean;
  summary?: EcomOrder['summary'];
  shipping?: {
    address?: {
      name?: string;
      phone?: string;
      address?: string;
      city?: string;
      district?: string;
      ward?: string;
      note?: string;
    };
    shipping_mode?: 'self' | 'carrier';
    pickup_location_id?: string | { _id: string };
    shipping_partner?: {
      name?: string;
      order_id?: string;
      tracking_url?: string;
    };
    // Mọi hãng ngoài sàn ghi dưới CHUNG một khoá `service_label`; hãng nào là
    // hãng nào thì đọc `template_name`/`carrier` trong bản ghi.
    labels?: PackingLabelMap;
  };
  shipping_order?: {
    _id?: string;
    order_number?: string;
    state?: string;
    shipping_partner?: {
      name?: ShippingPartnerName;
      order_id?: string;
    };
  };
}

export interface OffPlatformFulfillmentFilter extends WarehouseWorkspaceFilter {
  date_from?: string;
  date_to?: string;
}

export interface OffPlatformFulfillmentListResponse {
  data: OffPlatformFulfillmentOrder[];
  total: number;
  page: number;
  limit: number;
}

export interface OffPlatformPickupLocation {
  _id: string;
  name?: string;
  contact_name?: string;
  phone?: string;
  /**
   * BE trả kèm để lọc theo chi nhánh — một chi nhánh có nhiều nơi lấy hàng nên
   * danh sách phẳng không đủ để chọn đúng. `store_id` được populate nên có thể
   * là object; giữ cả hai dạng đúng như dữ liệu thật.
   */
  store_id?: string | { _id: string; name?: string };
  state?: number;
  is_default?: boolean;
  address?: string;
}

export const OFF_PLATFORM_PARTNERS: Array<{ value: ShippingPartnerName; label: string; disabled?: boolean; tooltip?: string }> = [
  { value: ShippingPartnerName.GHTK, label: 'GHTK' },
  { value: ShippingPartnerName.BEST, label: 'BEST Express' },
  { value: ShippingPartnerName.MANUAL, label: 'Thủ công' },
  { value: ShippingPartnerName.AHAMOVE, label: 'Ahamove', disabled: true, tooltip: 'Chưa tích hợp' },
  { value: ShippingPartnerName.VIETTELPOST, label: 'Viettel Post', disabled: true, tooltip: 'Chưa tích hợp' },
];

/**
 * Giá trị filter phải khớp `shipping.shipping_partner.name` trong Order.
 * `carrier.label` chỉ là tên hiển thị (ví dụ `BEST Express`), còn Settings
 * trả mã provider dạng thường (`best`).
 */
export function mapOffPlatformCarrierFilterValue(carrier: { provider?: string; label?: string }): string {
  const provider = carrier.provider?.trim().toLowerCase();
  const partner = OFF_PLATFORM_PARTNERS.find((option) => option.value.toLowerCase() === provider);
  return partner?.value ?? carrier.label?.trim() ?? provider?.toUpperCase() ?? '';
}

const workspaceBasePath: Record<WarehouseWorkspaceKey, string> = {
  'init-logistic': '/orders/fulfillment/init-logistic',
  'order-packing': '/orders/fulfillment/order-packing',
  'order-sending': '/orders/fulfillment/order-sending',
};

export function mapOffPlatformFulfillmentParams(params: OffPlatformFulfillmentFilter): Record<string, unknown> {
  return {
    page: params.page,
    limit: params.limit,
    q: params.search,
    date_from: params.created_from,
    date_to: params.created_to,
    display_status: params.display_status,
    unprinted_only: params.unprinted_only,
    carrier: params.carrier,
    warehouse_id: params.warehouse_id,
  };
}

export function toEcomOrderView(order: OffPlatformFulfillmentOrder): EcomOrder {
  const partner = order.shipping?.shipping_partner;
  const fulfillmentProgress = order.fulfillment_progress ?? FulfillmentProgress.SHIPMENT_PENDING;
  const isPendingCarrierConfirmation = order.display_substatus === 'Chờ hãng xác nhận';
  const trackingNumber = partner?.order_id;
  const packingScanLogs = order.fulfillment?.packing_scan_logs?.map((log): EcomScanLogEntry => ({
    ...log,
    scan_stage: 'packing',
  }));
  const shipmentStatus = isPendingCarrierConfirmation
    ? 'shipment_pending'
    : undefined;
  return {
    _id: order._id,
    order_number: order.order_number,
    store_id: order.store_id,
    channel: 'offline',
    channel_order_number: order.order_number,
    channel_order_state: order.state,
    shop_id: '',
    shop_name: 'Kênh ngoài sàn',
    state: order.state,
    fulfillment_state: fulfillmentProgress,
    shipment_request: order.shipment_request,
    shipment_request_timed_out: order.shipment_request_timed_out,
    display_status: order.display_status ?? fulfillmentProgress,
    display_status_key: order.display_status_key ?? fulfillmentProgress,
    display_substatus: order.display_substatus,
    display_substatus_type: order.display_substatus_type,
    warehouse_state: order.shipping_state ?? order.state,
    order_date: order.created_at ?? '',
    created_at: order.created_at ?? '',
    updated_at: order.created_at ?? '',
    pre_order_flag: false,
    reservation: { warehouse_id: '', status: 'unknown' },
    reservation_status: 'unknown',
    exceptions: [],
    items: order.items ?? [],
    shipping_note: order.shipping_note,
    notes: order.notes,
    tags: order.tags,
    skipped: order.skipped,
    shipping: {
      address: {
        name: order.shipping?.address?.name ?? order.receiver_name ?? '-',
        phone: order.shipping?.address?.phone ?? '',
        address: order.shipping?.address?.address ?? '',
        city: order.shipping?.address?.city ?? '',
        district: order.shipping?.address?.district ?? '',
        ward: order.shipping?.address?.ward ?? '',
        note: order.shipping?.address?.note,
      },
      shipping_mode: order.shipping?.shipping_mode,
      pickup_location_id: order.shipping?.pickup_location_id,
      shipping_partner: order.shipping?.shipping_partner,
      provider_name: partner?.name,
      carrier: partner?.name,
      tracking_number: trackingNumber,
      labels: order.shipping?.labels,
      shipments: trackingNumber ? [{ shipment_id: trackingNumber, source: 'offplatform', package_code: trackingNumber, tracking_number: trackingNumber, carrier: partner?.name, status: shipmentStatus, labels: order.shipping?.labels }] : [],
      is_buyer_masked: false,
      unmask_status: 'plain',
    },
    shipping_info: { fulfillment_progress: fulfillmentProgress, shipping_state: order.shipping_state },
    is_printed: order.is_printed ?? order.fulfillment?.label_printed,
    payment: {},
    summary: order.summary ?? {},
    state_history: [],
    fulfillment_history: [],
    process_logs: [],
    packing: order.fulfillment
      ? {
          status: order.fulfillment.status,
          packed_at: order.fulfillment.packed_at,
          scan_logs: packingScanLogs,
        }
      : undefined,
    version_certain: true,
  };
}

export const offPlatformFulfillmentApi = {
  createExportSlips: async (payload: { order_ids: string[] }) => {
    const { data } = await api.post('/orders/fulfillment/init-logistic/export-slips', payload);
    return data as BatchActionResult;
  },
  createPrintJob: async (payload: { order_ids: string[] }) => {
    const { data } = await api.post('/orders/fulfillment/init-logistic/print-jobs', payload);
    return data as BatchActionResult;
  },
  completePacking: async (orderId: string): Promise<BatchPackingCompletionResponse> => {
    const { data } = await api.post(`/orders/fulfillment/order-packing/${orderId}/complete`);
    return data as BatchPackingCompletionResponse;
  },
  scanPacking: async (orderId: string, payload: OffPlatformPackingScanPayload): Promise<OffPlatformPackingScanResult> => {
    const { data } = await api.post(`/orders/fulfillment/order-packing/${orderId}/scan`, payload);
    return data as OffPlatformPackingScanResult;
  },
};

export function useOffPlatformFulfillmentOrders(
  workspace: WarehouseWorkspaceKey,
  params: OffPlatformFulfillmentFilter,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: ['orders', 'offplatform-fulfillment', workspace, params],
    queryFn: async () => {
      const { data } = await api.get(`${workspaceBasePath[workspace]}/orders`, { params: mapOffPlatformFulfillmentParams(params) });
      const response = data as OffPlatformFulfillmentListResponse;
      return { ...response, data: response.data.map(toEcomOrderView) };
    },
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval,
    staleTime: 0,
  });
}

export function useCreateOffPlatformExportSlips() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: offPlatformFulfillmentApi.createExportSlips,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] });
      message.success('Đã tạo phiếu xuất ngoài sàn');
    },
    onError: () => message.error('Tạo phiếu xuất ngoài sàn thất bại'),
  });
}

export function useCreateOffPlatformPrintJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: offPlatformFulfillmentApi.createPrintJob,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] });
      message.success('Đã in phiếu ngoài sàn');
    },
    onError: () => message.error('In phiếu ngoài sàn thất bại'),
  });
}

export function useCompleteOffPlatformPacking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: offPlatformFulfillmentApi.completePacking,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] }),
  });
}

export function useScanOffPlatformPacking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: OffPlatformPackingScanPayload }) =>
      offPlatformFulfillmentApi.scanPacking(orderId, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] }),
  });
}

export function useMarkOffPlatformPacked() {
  return useCompleteOffPlatformPacking();
}

export function useBatchCreateOffPlatformShipment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { order_ids: string[]; shipping_partner: ShippingPartnerName; pickup_location_id?: string; note?: string }) => {
      const { data } = await api.post('/orders/fulfillment/init-logistic/batch-shipment-request', payload);
      return data as BatchActionResult;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] });
      message.success('Đã tạo vận đơn ngoài sàn');
    },
    onError: () => message.error('Tạo vận đơn ngoài sàn thất bại'),
  });
}

export function useOffPlatformPickupLocations(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['orders', 'offplatform-fulfillment', 'pickup-locations'],
    queryFn: async () => {
      const { data } = await api.get('/orders/fulfillment/pickup-locations');
      return data as { data: OffPlatformPickupLocation[] };
    },
    enabled: options.enabled ?? true,
  });
}

export function useMarkOffPlatformPickedUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data } = await api.post(`/orders/fulfillment/order-sending/${orderId}/mark-picked-up`);
      return data as { message: string };
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] }),
  });
}
